import React, { useEffect, useMemo, useState } from 'react';
import { Pagination, message } from 'antd';
import { Ban, ChevronRight, Download, Eye, IdCard, LayoutDashboard, LucideIcon, Pencil, Search, Target, TrendingUp, UserCheck, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Spinner from '@/components/Spinner';
import { getCmoList, getCmoStats } from '@/api/Admin/cmo';
import { formatCurrency, formatDate } from '@/lib/format';
import { DirectorStatus } from './data';

interface GdkdRow {
    id: string;
    name: string;
    initials: string;
    cccd: string;
    date: string;
    revenue: string;
    members: number;
    status: DirectorStatus;
}

interface GdkdSummary {
    total: number;
    totalActive: number;
    totalRevenue: number;
    totalPaidCommission: number;
}

type ExportColumnKey = 'id' | 'name' | 'cccd' | 'date' | 'revenue' | 'members' | 'status';

const EXPORT_COLUMNS: { key: ExportColumnKey; label: string }[] = [
    { key: 'id', label: 'Mã GĐKD' },
    { key: 'name', label: 'Giám đốc kinh doanh' },
    { key: 'cccd', label: 'CCCD/CMND' },
    { key: 'date', label: 'Ngày tham gia' },
    { key: 'revenue', label: 'Tổng doanh số' },
    { key: 'members', label: 'Số thành viên' },
    { key: 'status', label: 'Trạng thái' },
];

const DEFAULT_EXPORT_COLUMN_LABELS = EXPORT_COLUMNS.reduce((acc, column) => {
    acc[column.key] = column.label;
    return acc;
}, {} as Record<ExportColumnKey, string>);

const DEFAULT_SELECTED_EXPORT_COLUMNS = EXPORT_COLUMNS.map((column) => column.key);

type RevenueRangeFilter = '' | 'under_1b' | '1b_to_2b' | 'over_2b';
type StatusFilter = '' | 'active' | 'inactive';

function getInitials(name: string): string {
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
}

function normalizeDirectorStatus(status: string): DirectorStatus {
    const normalizedStatus = status.trim().toLowerCase();

    return normalizedStatus === 'active' || normalizedStatus === 'hoạt động'
        ? 'Hoạt động'
        : 'Ngừng hoạt động';
}

function getExportColumnValue(row: GdkdRow, columnKey: ExportColumnKey) {
    switch (columnKey) {
        case 'id':
            return row.id;
        case 'name':
            return row.name;
        case 'cccd':
            return row.cccd;
        case 'date':
            return row.date;
        case 'revenue':
            return row.revenue;
        case 'members':
            return row.members;
        case 'status':
            return row.status;
        default:
            return '';
    }
}

function SummaryCard({
    title,
    value,
    subtext,
    icon: Icon,
    iconBgColor,
    iconColor,
    trend,
    trendColor,
}: {
    title: string;
    value: string;
    subtext: string;
    icon: LucideIcon;
    iconBgColor: string;
    iconColor: string;
    trend?: string;
    trendColor?: string;
}) {
    return (
        <div className="flex h-full flex-col justify-between rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
            <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
                <div className="flex items-center justify-between gap-3">
                    <span className="text-2xl font-bold text-gray-800">{value}</span>
                    <div className={`rounded-lg p-2 ${iconBgColor}`}>
                        <Icon size={20} className={iconColor} />
                    </div>
                </div>
            </div>
            <div className="mt-4 flex items-center text-xs">
                {trend ? <span className={`mr-1 font-medium ${trendColor ?? 'text-gray-500'}`}>{trend}</span> : null}
                <span className="text-gray-500">{subtext}</span>
            </div>
        </div>
    );
}

function StatusBadge({ status }: { status: DirectorStatus }) {
    const isActive = status === 'Hoạt động';

    return (
        <span className={`whitespace-nowrap rounded-md px-2 py-1 text-center text-[11px] font-semibold ${isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
            {status}
        </span>
    );
}

export default function GdkdPage() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [revenueRangeFilter, setRevenueRangeFilter] = useState<RevenueRangeFilter>('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
    const [rows, setRows] = useState<GdkdRow[]>([]);
    const [summary, setSummary] = useState<GdkdSummary>({
        total: 0,
        totalActive: 0,
        totalRevenue: 0,
        totalPaidCommission: 0,
    });
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedExportColumns, setSelectedExportColumns] = useState<ExportColumnKey[]>(DEFAULT_SELECTED_EXPORT_COLUMNS);
    const [exportColumnLabels, setExportColumnLabels] = useState<Record<ExportColumnKey, string>>(DEFAULT_EXPORT_COLUMN_LABELS);

    const pageSize = 10;

    const filteredDirectors = useMemo(() => rows, [rows]);

    const handleToggleExportColumn = (columnKey: ExportColumnKey) => {
        setSelectedExportColumns((currentColumns) => (
            currentColumns.includes(columnKey)
                ? currentColumns.filter((currentColumn) => currentColumn !== columnKey)
                : [...currentColumns, columnKey]
        ));
    };

    const handleExportColumnLabelChange = (columnKey: ExportColumnKey, value: string) => {
        setExportColumnLabels((currentLabels) => ({
            ...currentLabels,
            [columnKey]: value,
        }));
    };

    const handleResetExportColumnLabels = () => {
        setExportColumnLabels(DEFAULT_EXPORT_COLUMN_LABELS);
    };

    const handleExportExcel = () => {
        if (selectedExportColumns.length === 0) {
            message.warning('Vui lòng chọn ít nhất một cột để xuất Excel.');
            return;
        }

        if (filteredDirectors.length === 0) {
            message.warning('Không có dữ liệu GĐKD để xuất.');
            return;
        }

        setExporting(true);

        try {
            const exportRows = filteredDirectors.map((row) => (
                selectedExportColumns.reduce((acc, columnKey) => {
                    const headerName = exportColumnLabels[columnKey].trim() || DEFAULT_EXPORT_COLUMN_LABELS[columnKey];
                    acc[headerName] = getExportColumnValue(row, columnKey);
                    return acc;
                }, {} as Record<string, string | number>)
            ));

            const worksheet = XLSX.utils.json_to_sheet(exportRows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Danh sach GDKD');
            XLSX.writeFile(workbook, `gdkd-list-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.xlsx`);
            setIsExportModalOpen(false);
            message.success('Xuất Excel thành công.');
        } catch (error) {
            console.error('Error exporting GDKD list:', error);
            message.error('Xuất Excel thất bại.');
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 1000);

        return () => window.clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, revenueRangeFilter, statusFilter]);

    useEffect(() => {
        const fetchCmoData = async () => {
            setLoading(true);
            try {
                const [statsResponse, listResponse] = await Promise.all([
                    getCmoStats(),
                    getCmoList(currentPage, pageSize, debouncedSearchTerm, revenueRangeFilter, statusFilter),
                ]);

                setSummary({
                    total: Number(statsResponse.total || 0),
                    totalActive: Number(statsResponse.total_active || 0),
                    totalRevenue: Number(statsResponse.total_revenue || 0),
                    totalPaidCommission: Number(statsResponse.total_paid_commission || 0),
                });

                const mappedRows: GdkdRow[] = (listResponse.data || []).map((item) => ({
                    id: String(item.id),
                    name: item.name,
                    initials: getInitials(item.name),
                    cccd: item.cccd || '-',
                    date: item.create_at ? formatDate(item.create_at) : '-',
                    revenue: formatCurrency(item.total_revenue || 0),
                    members: Number(item.total_members || 0),
                    status: normalizeDirectorStatus(item.status || ''),
                }));

                setRows(mappedRows);
                setTotalItems(Number(listResponse.meta?.total || 0));
            } catch (error) {
                console.error('Error fetching CMO listing data:', error);
                setRows([]);
                setTotalItems(0);
                setSummary({
                    total: 0,
                    totalActive: 0,
                    totalRevenue: 0,
                    totalPaidCommission: 0,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchCmoData();
    }, [currentPage, debouncedSearchTerm, revenueRangeFilter, statusFilter]);

    return (
        <div className="flex-1 overflow-y-auto p-4 dark:bg-neutral-950">
            <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-neutral-100">Quản lý Giám đốc kinh doanh</h1>
                    <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">Xem và cập nhật thông tin chi tiết của Giám đốc kinh doanh</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-neutral-400">
                    <LayoutDashboard size={14} />
                    <span>Dashboard</span>
                    <ChevronRight size={14} />
                    <span className="font-medium text-gray-800 dark:text-neutral-100">Quản lý GĐKD</span>
                </div>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                <SummaryCard title="TỔNG GIÁM ĐỐC KD" value={summary.total.toLocaleString('vi-VN')} subtext="tổng số GĐKD" icon={UserCheck} iconBgColor="bg-blue-50" iconColor="text-blue-500" />
                <SummaryCard title="ĐANG HOẠT ĐỘNG" value={summary.totalActive.toLocaleString('vi-VN')} subtext="GĐKD đang hoạt động" icon={TrendingUp} iconBgColor="bg-yellow-50" iconColor="text-yellow-600" />
                <SummaryCard title="TỔNG DOANH SỐ" value={formatCurrency(summary.totalRevenue)} subtext="doanh số toàn hệ thống" icon={Wallet} iconBgColor="bg-green-50" iconColor="text-green-500" />
                <SummaryCard title="TỔNG HOA HỒNG ĐÃ TRẢ" value={formatCurrency(summary.totalPaidCommission)} subtext="hoa hồng đã thanh toán" icon={Target} iconBgColor="bg-purple-50" iconColor="text-purple-500" />
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
                <div className="mb-6 flex flex-col items-center justify-between gap-4 p-4 pb-2 md:flex-row md:gap-0">
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500" size={18} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="Tìm kiếm theo tên hoặc ID Giám đốc kinh doanh..."
                            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                        />
                    </div>
                    <div className="flex w-full flex-wrap items-center gap-3 md:w-auto">
                        <select value={revenueRangeFilter} onChange={(event) => setRevenueRangeFilter(event.target.value as RevenueRangeFilter)} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-700 focus:border-blue-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100">
                            <option value="">Tất cả doanh số</option>
                            <option value="under_1b">Dưới 1 tỷ VNĐ</option>
                            <option value="1b_to_2b">Từ 1 - 2 tỷ VNĐ</option>
                            <option value="over_2b">Trên 2 tỷ VNĐ</option>
                        </select>
                        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-700 focus:border-blue-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100">
                            <option value="">Tất cả trạng thái</option>
                            <option value="active">Hoạt động</option>
                            <option value="inactive">Ngừng hoạt động</option>
                        </select>
                        <button onClick={() => setIsExportModalOpen(true)} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700">
                            <Download size={16} /> Xuất dữ liệu
                        </button>
                    </div>
                </div>

                <div className="h-1 min-w-[900px] border-b border-gray-300 dark:border-neutral-800" />

                <table className="min-w-[900px] w-full border-collapse text-left">
                    <thead>
                        <tr className="border-b border-gray-100 bg-gray-50/50 dark:border-neutral-800 dark:bg-neutral-900/50">
                            <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-neutral-400">Mã GĐKD</th>
                            <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-neutral-400">Giám đốc kinh doanh</th>
                            <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-neutral-400">CCCD/CMND</th>
                            <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-neutral-400">Ngày tham gia</th>
                            <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-neutral-400">Tổng doanh số</th>
                            <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-neutral-400">Số thành viên</th>
                            <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-neutral-400">Trạng thái</th>
                            <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-neutral-400">Thao tác</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
                        {loading ? (
                            <tr>
                                <td colSpan={8} className="px-6 py-10 text-center">
                                    <div className="flex justify-center">
                                        <Spinner />
                                    </div>
                                </td>
                            </tr>
                        ) : filteredDirectors.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500 dark:text-neutral-400">
                                    Không có dữ liệu GĐKD phù hợp.
                                </td>
                            </tr>
                        ) : filteredDirectors.map((row) => (
                            <tr key={row.id} className="transition-colors hover:bg-gray-50 dark:hover:bg-neutral-800/50">
                                <td className="px-6 py-4 text-sm font-medium text-gray-800 dark:text-neutral-100">{row.id}</td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                                            {row.initials}
                                        </div>
                                        <span className="text-sm font-semibold text-gray-800 dark:text-neutral-100">{row.name}</span>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-neutral-300">
                                    <div className="flex items-center gap-2">
                                        <IdCard size={18} className="text-gray-400 dark:text-neutral-500" />
                                        <span>{row.cccd}</span>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-neutral-300">{row.date}</td>
                                <td className="px-6 py-4 text-right text-sm font-semibold text-gray-800 dark:text-neutral-100">{row.revenue}</td>
                                <td className="px-6 py-4 text-center text-sm text-gray-600 dark:text-neutral-300">{row.members}</td>
                                <td className="px-6 py-4 text-center">
                                    <StatusBadge status={row.status} />
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center justify-center gap-3 text-gray-400 dark:text-neutral-500">
                                        <button onClick={() => navigate(`/admin/gdkd/${row.id}`)} className="transition-colors hover:text-blue-500" title="Xem chi tiết">
                                            <Eye size={18} />
                                        </button>
                                        <button className="transition-colors hover:text-blue-500" title="Chỉnh sửa">
                                            <Pencil size={18} />
                                        </button>
                                        <button className="transition-colors hover:text-red-500" title="Khóa">
                                            <Ban size={18} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {totalItems > 0 ? (
                    <div className="flex justify-end px-6 py-4">
                        <Pagination
                            current={currentPage}
                            total={totalItems}
                            pageSize={pageSize}
                            onChange={setCurrentPage}
                            showSizeChanger={false}
                        />
                    </div>
                ) : null}
            </div>

            {isExportModalOpen ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
                    <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl dark:bg-neutral-900">
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-neutral-800">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-neutral-100">Xuất Excel GĐKD</h2>
                                <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">Chọn cột dữ liệu và đổi tên tiêu đề cột trước khi tải file.</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={() => setSelectedExportColumns(DEFAULT_SELECTED_EXPORT_COLUMNS)} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800">Chọn tất cả</button>
                                <button type="button" onClick={() => setSelectedExportColumns([])} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800">Bỏ chọn</button>
                                <button type="button" onClick={handleResetExportColumnLabels} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800">Khôi phục tên cột</button>
                            </div>
                        </div>

                        <div className="space-y-4 px-6 py-5">
                            <div className="grid max-h-[420px] grid-cols-1 gap-3 overflow-y-auto pr-1 md:grid-cols-2">
                                {EXPORT_COLUMNS.map((column) => (
                                    <div key={column.key} className="rounded-lg border border-gray-200 p-4 dark:border-neutral-800 dark:bg-neutral-900/50">
                                        <label className="flex items-center gap-3 text-sm font-medium text-gray-800 dark:text-neutral-200">
                                            <input
                                                type="checkbox"
                                                checked={selectedExportColumns.includes(column.key)}
                                                onChange={() => handleToggleExportColumn(column.key)}
                                                className="h-4 w-4 rounded border-gray-300 dark:border-neutral-700"
                                            />
                                            <span>{column.label}</span>
                                        </label>
                                        <div className="mt-3">
                                            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-neutral-400">Tên cột trong file</label>
                                            <input
                                                type="text"
                                                value={exportColumnLabels[column.key]}
                                                onChange={(event) => handleExportColumnLabelChange(column.key, event.target.value)}
                                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-neutral-400">Đã chọn {selectedExportColumns.length}/{EXPORT_COLUMNS.length} cột. File xuất dùng đúng dữ liệu GĐKD đang hiển thị trong danh sách hiện tại.</p>
                        </div>

                        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-neutral-800">
                            <button type="button" onClick={() => setIsExportModalOpen(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800">Hủy</button>
                            <button type="button" onClick={handleExportExcel} disabled={exporting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400">{exporting ? 'Đang xuất...' : 'Xuất file'}</button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}